import type { ReactNode } from "react";
import {
  LuCheck,
  LuEye,
  LuFolder,
  LuGlobe,
  LuGrid2X2 as LuGrid,
  LuList,
  LuLock,
  LuPlus,
} from "react-icons/lu";
import slugify from "slugify";
import { useLocalStorage } from "usehooks-ts";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { CollectionIcon } from "@/components/shared/collection-icon";
import { DelegatedLink } from "@/components/shared/delegated-link";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useCollections } from "@/hooks/use-collections";
import { APP_ROUTES } from "@/utils/constants";
import { cn } from "@/utils/lib";
import { LocalStorageKeys } from "@/utils/local-storage-keys";

import { CollectionCard } from "./collection-card";
import { CollectionListItem } from "./collection-list-item";

function Loader({ count = 6 }: { count?: number }) {
  return (
    <div>
      {/* Navigation tabs */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex gap-2">
          <Skeleton className="h-10 w-16" />
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-20" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-16" />
          <Skeleton className="h-10 w-16" />
        </div>
      </div>

      {/* Header */}
      <div className="mb-4 flex w-full flex-col items-start gap-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-48" />
      </div>

      {/* Grid of cards */}
      <div
        className="
          grid grid-cols-1 gap-6
          @mq-lg:grid-cols-3
          @mq-md:grid-cols-2
        "
      >
        {Array.from({ length: count }, (_, index) => {
          // Vary the skeleton widths for a more realistic loading appearance
          const titleWidths = ["w-20", "w-48", "w-12", "w-8", "w-16", "w-52"];
          const descWidths = [
            "w-16",
            "w-full",
            "w-20",
            "w-16",
            "w-24",
            "w-full",
          ];
          const hasLongDesc = index % 3 === 1; // Every 3rd card has a longer description

          return (
            <div key={index} className="flex flex-col space-y-3 bg-card/50 p-4">
              <div className="flex items-center justify-between">
                <Skeleton className="size-4 bg-background" />
                <Skeleton className="size-4 bg-background" />
              </div>
              <Skeleton
                className={`
                  h-4 bg-background
                  ${titleWidths[index % titleWidths.length]}
                `}
              />
              {hasLongDesc ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full bg-background" />
                  <Skeleton className="h-4 w-24 bg-background" />
                </div>
              ) : (
                <Skeleton
                  className={`
                    h-4 bg-background
                    ${descWidths[index % descWidths.length]}
                  `}
                />
              )}
              <div className="mt-auto flex items-center justify-between pt-4">
                <Skeleton className="h-3 w-12 bg-background" />
                <Skeleton className="h-3 w-20 bg-background" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CollectionsPageHeader({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string | false;
}) {
  return (
    <div className="flex flex-row items-center gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-center gap-3">
          <div className="text-muted">{icon}</div>
          <h2 className="font-sans text-xl font-semibold text-muted-foreground">
            {title}
          </h2>
        </div>
        <p className="text-sm text-muted">{description}</p>
      </div>
    </div>
  );
}

export function CollectionsPage({
  userPublicKey,
  username,
}: {
  userPublicKey: string;
  username?: string;
}) {
  const [viewMode, setViewMode] = useLocalStorage(
    LocalStorageKeys.collectionsViewMode,
    "grid",
  );
  const [filter, setFilter] = useLocalStorage(
    LocalStorageKeys.collectionsFilter,
    "all",
  );
  const [visibility, setVisibility] = useLocalStorage(
    LocalStorageKeys.collectionsVisibility,
    "all",
  );
  const { collections, collectionsLoading, isOwner } = useCollections({
    userPublicKey: userPublicKey as string,
  });

  const filteredCollections = collections
    ?.filter((collection) => {
      if (filter === "all") return true;
      return collection.type === filter;
    })
    ?.filter((collection) => {
      if (!isOwner && collection.private) return false;
      if (visibility === "public") return !collection.private;
      if (visibility === "private") return collection.private;
      return true;
    });

  const collectionTypeHeader = () => {
    switch (filter) {
      case "bookmark":
        return (
          <CollectionsPageHeader
            icon={
              <CollectionIcon type={filter} className="size-5 text-muted" />
            }
            title="Bookmarks"
            description={
              isOwner &&
              "You can save any post as a bookmark to come back to it later."
            }
          />
        );
      case "playlist":
        return (
          <CollectionsPageHeader
            icon={
              <CollectionIcon type={filter} className="size-5 text-muted" />
            }
            title="Playlists"
            description={
              isOwner &&
              "You can add any video or audio post to make it easily playable."
            }
          />
        );
      case "gallery":
        return (
          <CollectionsPageHeader
            icon={
              <CollectionIcon type={filter} className="size-5 text-muted" />
            }
            title="Galleries"
            description={
              isOwner && "You can save any video or image as a gallery."
            }
          />
        );
      default:
        return (
          <CollectionsPageHeader
            icon={<CollectionIcon className="size-5 text-muted" />}
            title="Collections"
            description={
              isOwner && "Manage your bookmarks, playlists, and galleries."
            }
          />
        );
    }
  };

  const toggleItemClassName =
    "px-3 py-2 hover:bg-card hover:text-primary @mq-md:px-4 h-auto unforce-radius flex items-center gap-2 basis-auto border-t border-b border-t-border border-b-border data-pressed:bg-primary data-pressed:text-primary-foreground first:border-l first:border-l-border last:border-r last:border-r-border data-pressed:border-t-primary data-pressed:border-b-primary data-pressed:first:border-l-primary data-pressed:last:border-r-primary [&>span]:hidden data-pressed:[&>span]:inline @mq-md:[&>span]:inline data-pressed:[&>svg]:text-primary-foreground";
  const selectedToggleItemClassName =
    "overflow-hidden rounded-md border border-primary bg-primary text-primary-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground [&>svg]:text-primary-foreground hover:[&>svg]:text-primary-foreground";

  return (
    <div className="@container w-full p-6">
      <div
        className="
          mb-6 flex items-start gap-4
          @mq-sm:items-center
        "
      >
        {!collectionsLoading && !!collections?.length && collectionTypeHeader()}
        {isOwner && (
          <Button
            size="sm"
            disabled={collectionsLoading}
            className="ml-auto"
            onClick={() =>
              setDialog(DIALOG.CreateCollection, {
                open: true,
              })
            }
          >
            <LuPlus className="mr-2 size-4" />
            <span>New</span>
            <span
              className="
                hidden
                @mq-sm:inline-flex
              "
            >
              &nbsp;Collection
            </span>
          </Button>
        )}
      </div>
      {collectionsLoading && <Loader />}
      {!collectionsLoading && collections?.length === 0 && (
        <EmptyState
          image="/assets/empty-32.png"
          title={
            isOwner
              ? "You don't have any collections yet."
              : "This user has no collections."
          }
          description={
            isOwner ? "Create your first collection." : "Check back later."
          }
          classNames="my-20"
        />
      )}
      {!collectionsLoading && !!collections?.length && (
        <div className="w-full">
          <div className="flex flex-wrap gap-4">
            <ToggleGroup
              type="single"
              value={filter}
              onValueChange={(value) => {
                if (typeof value === "string" && value) {
                  setFilter(value);
                }
              }}
              className="
                w-full text-muted
                @mq-sm:w-auto
              "
            >
              <ToggleGroupItem value="all" className={toggleItemClassName}>
                <LuFolder className="size-5 text-muted" />
                <span>All</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="bookmark" className={toggleItemClassName}>
                <CollectionIcon type="bookmark" className="size-5 text-muted" />
                <span>Bookmarks</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="playlist" className={toggleItemClassName}>
                <CollectionIcon type="playlist" className="size-5 text-muted" />
                <span>Playlists</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="gallery" className={toggleItemClassName}>
                <CollectionIcon type="gallery" className="size-5 text-muted" />
                <span>Galleries</span>
              </ToggleGroupItem>
            </ToggleGroup>

            <div
              className="
                ml-auto flex w-full items-center gap-2
                @mq-sm:w-auto
              "
            >
              {isOwner && (
                <DropdownDrawer
                  historyName="collection-visibility"
                  title="Collection visibility"
                >
                  <DropdownDrawerTrigger
                    render={
                      <div
                        className={cn(
                          toggleItemClassName,
                          selectedToggleItemClassName,
                          `
                            cursor-pointer text-sm
                            @mq-sm:ml-auto
                          `,
                        )}
                      >
                        {visibility === "all" ? (
                          <LuEye className="size-5 text-muted" />
                        ) : visibility === "public" ? (
                          <LuGlobe className="size-5 text-muted" />
                        ) : (
                          <LuLock className="size-5 text-muted" />
                        )}
                        <div>
                          {visibility.charAt(0).toUpperCase() +
                            visibility.slice(1)}
                        </div>
                      </div>
                    }
                  />
                  <DropdownDrawerContent align="start">
                    <DropdownDrawerItem
                      onClick={() => setVisibility("all")}
                      className="cursor-pointer"
                    >
                      All
                      {visibility === "all" && (
                        <LuCheck className="ml-auto text-primary" />
                      )}
                    </DropdownDrawerItem>
                    <DropdownDrawerItem
                      onClick={() => setVisibility("public")}
                      className="cursor-pointer"
                    >
                      Public
                      {visibility === "public" && (
                        <LuCheck className="ml-auto text-primary" />
                      )}
                    </DropdownDrawerItem>
                    <DropdownDrawerItem
                      onClick={() => setVisibility("private")}
                      className="cursor-pointer"
                    >
                      Private
                      {visibility === "private" && (
                        <LuCheck className="ml-auto text-primary" />
                      )}
                    </DropdownDrawerItem>
                  </DropdownDrawerContent>
                </DropdownDrawer>
              )}

              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={(value) => {
                  if (typeof value === "string" && value) {
                    setViewMode(value);
                  }
                }}
                className="ml-auto"
              >
                <ToggleGroupItem
                  value="grid"
                  aria-label="Grid view"
                  className={toggleItemClassName}
                >
                  <LuGrid className="size-5 text-muted" />
                  <span>Grid</span>
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="list"
                  aria-label="List view"
                  className={toggleItemClassName}
                >
                  <LuList className="size-5 text-muted" />
                  <span>List</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
          <div className="mt-6">
            {viewMode === "grid" ? (
              <div
                className="
                  grid grid-cols-1 gap-6
                  @mq-lg:grid-cols-3
                  @mq-sm:grid-cols-2
                "
              >
                {filteredCollections?.map((collection) => (
                  <DelegatedLink
                    key={collection.id}
                    href={`${APP_ROUTES.collections}/${username || userPublicKey}/${slugify(collection.name)}-${collection.id}`}
                  >
                    <CollectionCard collection={collection} isOwner={isOwner} />
                  </DelegatedLink>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCollections?.map((collection) => (
                  <DelegatedLink
                    key={collection.id}
                    href={`${APP_ROUTES.collections}/${username || userPublicKey}/${slugify(collection.name)}-${collection.id}`}
                  >
                    <CollectionListItem collection={collection} />
                  </DelegatedLink>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {!collectionsLoading &&
        !!collections?.length &&
        !filteredCollections?.length && (
          <EmptyState
            image="/assets/empty-32.png"
            title="No collections found"
            description="Try changing the filter."
            classNames="my-20"
          />
        )}
    </div>
  );
}
